import { setDefaultOrganization } from "../components/CSMShopfloorHeader/CSMShopfloorHeader";
import { SupportTarget } from "../utility/supportTenant";
import { currentRequestScope, RequestScope, setAmbientEmail } from "./ApiService";
import { setActiveRegion } from "./regionRuntime";
import { sameRequestScope } from "./requestScope";
import { dropJobActionIntent } from "./supportActionBus";
import { dropSettingsPrefill } from "./supportSettingsBus";

/**
 * Side effects of the support-target decision, kept out of utility/supportTenant.ts so the
 * decision itself stays pure and cheap to test.
 *
 * Lives under services/ (not utility/) because it mutates the API + realtime runtime state.
 * It must NOT be imported by regionRuntime or ApiService — ApiService already imports
 * regionRuntime, and this module imports both, so a reverse edge would be a cycle.
 */

/** The scope the last `applySupportTarget` left the session in, or null before the first. */
let lastApplied: RequestScope | null = null;

/**
 * Connect the session to `target`, in this order:
 *   1. region  — so the NEXT BFF call already goes to the org's Function App;
 *   2. header label — what the user sees they are connected to;
 *   3. ambient identity — the RLS impersonation, or the lack of one.
 *
 * `target.support === null` means the user's own dedicated environment, where they act as
 * themselves. That case must still be written: it is what DROPS a previous org's
 * impersonation when they switch back home. Skipping it would leave them reading another
 * tenant's rows under a header that says otherwise.
 *
 * `target` must come from resolveSupportTarget / resolveAutoTenant, where the CORP-only gate
 * lives.
 */
export function applySupportTarget(target: SupportTarget): void {
  // Compared with the organisation applied last, not with the scope just before this call:
  // PrivateRoute's cached path puts the active region back to the user's home first, on every
  // navigation, so for an organisation supported from another region the scope here always
  // looks as if it moved. With nothing applied yet in this page, the scope in use is the one
  // being left.
  const before = lastApplied ?? currentRequestScope();
  setActiveRegion(target.region);
  setDefaultOrganization(target.code);
  setAmbientEmail(target.support);
  lastApplied = currentRequestScope();

  // An assistant intent left unclaimed belongs to the organisation it was asked in. Dropped on
  // a real change only: PrivateRoute applies the same organisation again on every navigation,
  // the one a chip makes to its order included.
  if (!sameRequestScope(before, lastApplied)) {
    dropJobActionIntent();
    dropSettingsPrefill();
  }
}

/** Test seam. Forgets the organisation applied last. */
export function __resetSupportTenantRuntime(): void {
  lastApplied = null;
}
